import type { Project, Task, Baseline, BaselineTask, BaselineSnapshot, ChangeRequest } from '@/types';
import { daysBetween } from './format';

/**
 * Build a BaselineSnapshot from a project's CURRENT state (budget, dates,
 * scope, objectives, completion, and a copy of the WBS). Shared by the manual
 * "capture baseline" modal and the automatic re-baseline on CR approval, so
 * both snapshot identically.
 */
export function buildSnapshot(project: Project): BaselineSnapshot {
  const wbsTasks: BaselineTask[] = (project.wbsTasks ?? []).map((t) => ({
    taskId: t.taskId,
    wbs: t.wbs,
    task: t.task,
    baselineStartDate: t.startDate,
    baselineEndDate: t.endDate,
    baselineDuration: t.duration,
    baselineCost: t.cost ?? 0,
    baselinePredecessors: t.predecessors,
    baselineResources: [...(t.resources ?? [])],
  }));
  return {
    budget: project.budget ?? 0,
    startDate: project.startDate,
    endDate: project.endDate,
    scope: project.scope ?? '',
    objectives: [...(project.objectives ?? [])],
    completion: project.completion ?? 0,
    wbsTasks,
    taskCount: wbsTasks.length,
  };
}

/** Add `days` (may be negative) to a 'YYYY-MM-DD' date, returning 'YYYY-MM-DD'.
 *  Parsed as local midnight to match the rest of the app's date handling. */
export function shiftIsoDate(iso: string | null | undefined, days: number): string {
  if (!iso) return iso ?? '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + (days || 0));
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/**
 * Compute the project changes + new baseline when a re-baseline change request
 * is approved. Applies the CR's impact deltas to the project:
 *   - budget  += costImpact
 *   - endDate shifts by scheduleImpact days
 *   - scope   ← scopeChange (only when non-empty)
 * then snapshots the UPDATED project as a new 'rebaseline' baseline linked to
 * the CR. Pure — the caller persists `updates` (updateProject) and `baseline`
 * (createBaseline). The WBS is snapshotted as-is; task dates aren't auto-shifted
 * (the PM adjusts the schedule, which the new baseline then captures).
 */
export function applyCrRebaseline(
  project: Project,
  cr: ChangeRequest,
  opts: { by?: string; now?: string } = {},
): { updates: Partial<Project>; baseline: Baseline } {
  const now = opts.now ?? new Date().toISOString();
  const updates: Partial<Project> = {};
  updates.budget = (project.budget ?? 0) + (cr.costImpact ?? 0);
  if (cr.scheduleImpact) updates.endDate = shiftIsoDate(project.endDate, cr.scheduleImpact);
  if (cr.scopeChange && cr.scopeChange.trim()) updates.scope = cr.scopeChange.trim();

  const updatedProject: Project = { ...project, ...updates };
  const baseline: Baseline = {
    id: `BL-${Date.now().toString(36).toUpperCase()}`,
    name: `Re-baseline — ${cr.id}`,
    type: 'rebaseline',
    setDate: now,
    setBy: opts.by ?? 'Change control',
    reason: `Auto-captured on approval of ${cr.id}: ${cr.title}`,
    linkedChangeRequest: cr.id,
    approvedBy: opts.by ?? '',
    status: 'active',
    snapshot: buildSnapshot(updatedProject),
  };
  return { updates, baseline };
}

/**
 * Get the active baseline of a project, if any.
 */
export function getActiveBaseline(project: Project | undefined): Baseline | null {
  if (!project || !project.baselines) return null;
  return project.baselines.find((b) => b.id === project.activeBaselineId) ?? null;
}

/**
 * Look up a task in a baseline snapshot by its taskId.
 */
export function findBaselineTask(
  baseline: Baseline | null,
  taskId: number,
): BaselineTask | null {
  if (!baseline || !baseline.snapshot.wbsTasks) return null;
  return baseline.snapshot.wbsTasks.find((t) => t.taskId === taskId) ?? null;
}

export interface TaskVariance {
  isNew: boolean;
  startDelta: number | null;
  endDelta: number | null;
  durationDelta: number | null;
  baselineStart: string | null;
  baselineEnd: string | null;
  baselineDuration: number | null;
}

/**
 * Compute task variance against the active baseline.
 * Returns null if no active baseline.
 */
export function computeTaskVariance(
  project: Project,
  task: Task,
): TaskVariance | null {
  const active = getActiveBaseline(project);
  if (!active) return null;

  const bt = findBaselineTask(active, task.taskId);
  if (!bt) {
    return {
      isNew: true,
      startDelta: null,
      endDelta: null,
      durationDelta: null,
      baselineStart: null,
      baselineEnd: null,
      baselineDuration: null,
    };
  }

  return {
    isNew: false,
    startDelta: daysBetween(bt.baselineStartDate, task.startDate),
    endDelta: daysBetween(bt.baselineEndDate, task.endDate),
    durationDelta: (task.duration ?? 0) - (bt.baselineDuration ?? 0),
    baselineStart: bt.baselineStartDate,
    baselineEnd: bt.baselineEndDate,
    baselineDuration: bt.baselineDuration,
  };
}

/**
 * Severity tier for a variance amount (in days).
 * Negative = early (good), zero = on plan, positive = late.
 */
export type VarianceSeverity = 'on-plan' | 'early' | 'minor' | 'medium' | 'high';

export function classifyVariance(delta: number | null): VarianceSeverity {
  if (delta === null || delta === 0) return delta === 0 ? 'on-plan' : 'on-plan';
  if (delta < 0) return 'early';
  if (delta >= 10) return 'high';
  if (delta >= 5) return 'medium';
  return 'minor';
}

/**
 * Project-level variance summary.
 */
export interface ProjectVariance {
  baseline: Baseline;
  tasksAdded: number;
  tasksRemoved: number;
  tasksRescheduled: number;
  budgetDelta: number;
  startDelta: number | null;
  endDelta: number | null;
  objCountDelta: number;
  scopeChanged: boolean;
}

export function computeProjectVariance(project: Project): ProjectVariance | null {
  const active = getActiveBaseline(project);
  if (!active) return null;

  const s = active.snapshot;
  const currentTaskIds = new Set((project.wbsTasks ?? []).map((t) => t.taskId));
  const baselineTaskIds = new Set(s.wbsTasks.map((t) => t.taskId));

  const tasksAdded = (project.wbsTasks ?? []).filter(
    (t) => !baselineTaskIds.has(t.taskId),
  ).length;
  const tasksRemoved = s.wbsTasks.filter((t) => !currentTaskIds.has(t.taskId)).length;
  const tasksRescheduled = (project.wbsTasks ?? []).filter((t) => {
    const bt = findBaselineTask(active, t.taskId);
    if (!bt) return false;
    return bt.baselineStartDate !== t.startDate || bt.baselineEndDate !== t.endDate;
  }).length;

  return {
    baseline: active,
    tasksAdded,
    tasksRemoved,
    tasksRescheduled,
    budgetDelta: (project.budget ?? 0) - (s.budget ?? 0),
    startDelta: daysBetween(s.startDate, project.startDate),
    endDelta: daysBetween(s.endDate, project.endDate),
    objCountDelta: (project.objectives ?? []).length - s.objectives.length,
    scopeChanged: (project.scope ?? '') !== (s.scope ?? ''),
  };
}
